(() => {
  'use strict';

  const $ = (selector, context = document) => context.querySelector(selector);
  const $$ = (selector, context = document) => Array.from(context.querySelectorAll(selector));
  const body = document.body;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const routes = window.levoreTheme?.routes || {};
  let lastFocused = null;

  /* LEVORE V10: persistenter Schwarz-/Weiß-Modus. */
  const root = document.documentElement;
  const modeButtons = () => $$('[data-lp-mode-select]');
  const syncModeUI = mode => {
    modeButtons().forEach(button => button.setAttribute('aria-pressed', String(button.dataset.lpModeSelect === mode)));
    const meta = $('#LPThemeColor');
    if (meta) meta.setAttribute('content', window.levoreTheme?.colors?.[mode] || getComputedStyle(root).getPropertyValue('--bg').trim());
  };
  const setLevoreMode = (mode, persist = true) => {
    if (!window.levoreTheme?.dualMode || !['dark','light'].includes(mode)) return;
    root.classList.remove('lp-mode-changing');
    void root.offsetWidth;
    root.classList.add('lp-mode-changing');
    window.setTimeout(() => root.classList.remove('lp-mode-changing'), reducedMotion ? 0 : 680);
    root.dataset.lpMode = mode;
    if (persist) { try { localStorage.setItem('levore-color-mode', mode); } catch (_) {} }
    syncModeUI(mode);
    document.dispatchEvent(new CustomEvent('levore:modechange', { detail: { mode } }));
  };
  modeButtons().forEach(button => button.addEventListener('click', () => setLevoreMode(button.dataset.lpModeSelect)));
  syncModeUI(root.dataset.lpMode || window.levoreTheme?.defaultMode || 'dark');
  $$('[data-lp-mode-link]').forEach(link => link.addEventListener('click', () => {
    try { localStorage.setItem('levore-color-mode', link.dataset.lpModeLink); } catch (_) {}
  }));

  const escapeHTML = (value = '') => String(value).replace(/[&<>'"]/g, char => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  })[char]);

  const formatMoney = cents => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
        style: 'currency',
        currency: window.levoreTheme?.currency || window.Shopify?.currency?.active || 'EUR'
      }).format(Number(cents || 0) / 100);
    } catch (_) {
      return `${(Number(cents || 0) / 100).toFixed(2)} €`;
    }
  };

  const focusable = container => $$('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', container)
    .filter(el => !el.hasAttribute('hidden'));

  const syncBodyLock = () => {
    const openOverlay = $('.mobile-menu.is-open, .drawer.is-open, .search-overlay.is-open, .product-lightbox.is-open');
    body.classList.toggle('locked', Boolean(openOverlay));
  };

  const setOpen = (element, open, trigger = null) => {
    if (!element) return;
    if (open) lastFocused = trigger || document.activeElement;
    element.classList.toggle('is-open', open);
    element.setAttribute('aria-hidden', String(!open));
    syncBodyLock();

    if (open) {
      window.setTimeout(() => focusable(element)[0]?.focus(), reducedMotion ? 0 : 180);
    } else if (lastFocused instanceof HTMLElement) {
      lastFocused.focus({ preventScroll: true });
    }
  };

  const mobileMenu = $('[data-mobile-menu]');
  const menuToggle = $('[data-menu-toggle]');
  menuToggle?.addEventListener('click', () => {
    setOpen(mobileMenu, true, menuToggle);
    menuToggle.setAttribute('aria-expanded', 'true');
  });
  $('[data-menu-close]')?.addEventListener('click', () => {
    setOpen(mobileMenu, false);
    menuToggle?.setAttribute('aria-expanded', 'false');
  });

  const searchOverlay = $('#SearchOverlay');
  $$('[data-search-open]').forEach(button => button.addEventListener('click', () => setOpen(searchOverlay, true, button)));
  $$('[data-search-close]').forEach(button => button.addEventListener('click', () => setOpen(searchOverlay, false)));

  const cartDrawer = $('#CartDrawer');
  $$('[data-cart-open]').forEach(button => button.addEventListener('click', async () => {
    setOpen(cartDrawer, true, button);
    await renderCart();
  }));
  $$('[data-drawer-close]').forEach(button => button.addEventListener('click', () => setOpen(cartDrawer, false)));

  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    if (mobileMenu?.classList.contains('is-open')) {
      setOpen(mobileMenu, false);
      menuToggle?.setAttribute('aria-expanded', 'false');
    }
    if (searchOverlay?.classList.contains('is-open')) setOpen(searchOverlay, false);
    if (cartDrawer?.classList.contains('is-open')) setOpen(cartDrawer, false);
  });

  document.addEventListener('keydown', event => {
    if (event.key !== 'Tab') return;
    const activeOverlay = $('.mobile-menu.is-open, .drawer.is-open, .search-overlay.is-open, .product-lightbox.is-open');
    if (!activeOverlay) return;
    const items = focusable(activeOverlay);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  const header = $('[data-header]');
  const onScroll = () => header?.classList.toggle('is-scrolled', window.scrollY > 18);
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });

  if ('IntersectionObserver' in window && !reducedMotion) {
    const revealObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -40px' });
    $$('[data-reveal]').forEach(element => revealObserver.observe(element));
  } else {
    $$('[data-reveal]').forEach(element => element.classList.add('is-visible'));
  }

  $$('[data-gallery-src]').forEach(button => button.addEventListener('click', () => {
    const gallery = button.closest('.featured-lp__gallery');
    const image = $('.featured-lp__main img', gallery || document);
    if (!image) return;
    image.style.opacity = '0';
    window.setTimeout(() => {
      image.src = button.dataset.gallerySrc;
      image.alt = $('img', button)?.alt || image.alt;
      image.style.opacity = '1';
    }, reducedMotion ? 0 : 170);
  }));

  const updateCartCount = count => $$('[data-cart-count]').forEach(element => { element.textContent = count; });

  async function fetchCart() {
    const response = await fetch(`${routes.cart || '/cart'}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart request failed');
    return response.json();
  }

  async function changeCart(line, quantity) {
    const response = await fetch(routes.cartChange || '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity })
    });
    if (!response.ok) throw new Error('Cart update failed');
    return response.json();
  }

  async function renderCart(cartData = null) {
    const container = $('#CartDrawerContent');
    if (!container) return;
    container.innerHTML = '<div class="drawer__loading">Deine Auswahl wird geladen …</div>';

    try {
      const cart = cartData || await fetchCart();
      updateCartCount(cart.item_count);

      if (!cart.item_count) {
        container.innerHTML = '<div class="empty-state"><p>Dein Warenkorb ist leer.</p><a class="button button--gold button--full" href="/collections/all">KOLLEKTION ENTDECKEN</a></div>';
        return;
      }

      const items = cart.items.map((item, index) => {
        const image = item.image ? `<img src="${escapeHTML(item.image)}&width=240" alt="${escapeHTML(item.product_title)}" width="92" height="118">` : '<div class="drawer-item__placeholder"></div>';
        const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<p>${escapeHTML(item.variant_title)}</p>` : '';
        return `<article class="drawer-item" data-cart-line="${index + 1}">
          ${image}
          <div>
            <h3>${escapeHTML(item.product_title)}</h3>
            ${variant}
            <p>${formatMoney(item.final_price)}</p>
            <div class="drawer-item__qty" aria-label="Menge">
              <button type="button" data-cart-decrease aria-label="Menge verringern">−</button>
              <span>${item.quantity}</span>
              <button type="button" data-cart-increase aria-label="Menge erhöhen">+</button>
            </div>
            <button class="drawer-item__remove" type="button" data-cart-remove>Entfernen</button>
          </div>
          <strong>${formatMoney(item.final_line_price)}</strong>
        </article>`;
      }).join('');

      container.innerHTML = `${items}
        <div class="drawer-total">
          <p><span>ZWISCHENSUMME</span><strong>${formatMoney(cart.total_price)}</strong></p>
          <a class="button button--gold button--full" href="/checkout">SICHER ZUR KASSE</a>
          <a class="button button--line button--full" href="${routes.cart || '/cart'}">WARENKORB ANSEHEN</a>
        </div>`;

      $$('[data-cart-line]', container).forEach(itemElement => {
        const line = Number(itemElement.dataset.cartLine);
        const quantity = Number($('.drawer-item__qty span', itemElement)?.textContent || 1);
        $('[data-cart-decrease]', itemElement)?.addEventListener('click', async () => renderCart(await changeCart(line, Math.max(0, quantity - 1))));
        $('[data-cart-increase]', itemElement)?.addEventListener('click', async () => renderCart(await changeCart(line, quantity + 1)));
        $('[data-cart-remove]', itemElement)?.addEventListener('click', async () => renderCart(await changeCart(line, 0)));
      });
    } catch (error) {
      container.innerHTML = '<div class="empty-state"><p>Der Warenkorb konnte nicht geladen werden.</p><a class="button button--line" href="/cart">WARENKORB ANSEHEN</a></div>';
    }
  }

  $$('.js-product-form').forEach(form => form.addEventListener('submit', async event => {
    event.preventDefault();
    const button = $('button[type="submit"]', form);
    const originalText = button?.textContent || 'ADD TO BAG';
    if (button) {
      button.disabled = true;
      button.textContent = 'ADDING…';
    }

    try {
      const response = await fetch(routes.cartAdd || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      if (!response.ok) throw new Error('Add to cart failed');
      const cart = await fetchCart();
      if (button) button.textContent = 'ADDED';
      setOpen(cartDrawer, true, button);
      await renderCart(cart);
    } catch (error) {
      if (button) button.textContent = 'PLEASE TRY AGAIN';
    } finally {
      window.setTimeout(() => {
        if (button) {
          button.disabled = false;
          button.textContent = originalText;
        }
      }, 1300);
    }
  }));

  $$('[data-qty-minus]').forEach(button => button.addEventListener('click', () => {
    const input = $('input', button.parentElement);
    if (input) input.value = String(Math.max(1, Number(input.value || 1) - 1));
  }));
  $$('[data-qty-plus]').forEach(button => button.addEventListener('click', () => {
    const input = $('input', button.parentElement);
    if (input) input.value = String(Number(input.value || 1) + 1);
  }));

  $$('.product-main').forEach(root => {
    const variantSelect = $('[data-variant-select]', root);
    const optionGroups = $$('.variant-options', root);
    if (!variantSelect || !optionGroups.length) return;

    const syncVariant = () => {
      const selectedValues = optionGroups.map(group => $('input:checked', group)?.value || '');
      const option = Array.from(variantSelect.options).find(entry => {
        const values = (entry.dataset.options || '').split('||');
        return values.every((value, index) => value === selectedValues[index]);
      });
      if (!option) return;
      variantSelect.value = option.value;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', option.value);
      window.history.replaceState({}, '', url);
    };

    $$('input[type="radio"]', root).forEach(radio => radio.addEventListener('change', syncVariant));
  });


  /* Full-screen product image viewer with wheel, button, drag and pinch zoom. */
  const productZoomTriggers = $$('[data-product-zoom]');
  if (productZoomTriggers.length) {
    const lightbox = document.createElement('div');
    lightbox.id = 'ProductLightbox';
    lightbox.className = 'product-lightbox';
    lightbox.setAttribute('aria-hidden', 'true');
    lightbox.innerHTML = `
      <div class="product-lightbox__backdrop" data-product-zoom-close></div>
      <div class="product-lightbox__panel" role="dialog" aria-modal="true" aria-label="Produktbild vergrößert anzeigen">
        <div class="product-lightbox__toolbar">
          <span class="product-lightbox__brand">LEVORE PRIVÉ</span>
          <div class="product-lightbox__controls" aria-label="Bildzoom">
            <button type="button" class="product-lightbox__button" data-product-zoom-out aria-label="Verkleinern">−</button>
            <button type="button" class="product-lightbox__button" data-product-zoom-reset aria-label="Zoom zurücksetzen">1:1</button>
            <button type="button" class="product-lightbox__button" data-product-zoom-in aria-label="Vergrößern">+</button>
            <button type="button" class="product-lightbox__button product-lightbox__close" data-product-zoom-close aria-label="Großansicht schließen">×</button>
          </div>
        </div>
        <div class="product-lightbox__stage" data-product-zoom-stage>
          <img class="product-lightbox__image" data-product-zoom-image alt="" draggable="false">
        </div>
        <p class="product-lightbox__hint">Scroll, pinch or use the controls to zoom</p>
      </div>`;
    body.appendChild(lightbox);

    const stage = $('[data-product-zoom-stage]', lightbox);
    const zoomImage = $('[data-product-zoom-image]', lightbox);
    const zoomInButton = $('[data-product-zoom-in]', lightbox);
    const zoomOutButton = $('[data-product-zoom-out]', lightbox);
    const zoomResetButton = $('[data-product-zoom-reset]', lightbox);
    const pointers = new Map();
    let scale = 1;
    let translateX = 0;
    let translateY = 0;
    let dragStartX = 0;
    let dragStartY = 0;
    let dragOriginX = 0;
    let dragOriginY = 0;
    let pinchStartDistance = 0;
    let pinchStartScale = 1;

    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
    const pointerDistance = values => Math.hypot(values[0].x - values[1].x, values[0].y - values[1].y);

    const constrainPan = () => {
      if (!stage || !zoomImage || scale <= 1) {
        translateX = 0;
        translateY = 0;
        return;
      }
      const maxX = Math.max(0, (zoomImage.clientWidth * scale - stage.clientWidth) / 2);
      const maxY = Math.max(0, (zoomImage.clientHeight * scale - stage.clientHeight) / 2);
      translateX = clamp(translateX, -maxX, maxX);
      translateY = clamp(translateY, -maxY, maxY);
    };

    const renderZoom = (animate = true) => {
      scale = clamp(scale, 1, 4);
      constrainPan();
      stage?.classList.toggle('is-zoomed', scale > 1.01);
      if (zoomImage) {
        zoomImage.style.transition = animate && !reducedMotion ? 'transform .18s ease' : 'none';
        zoomImage.style.transform = `translate3d(${translateX}px, ${translateY}px, 0) scale(${scale})`;
      }
      if (zoomOutButton) zoomOutButton.disabled = scale <= 1.01;
      if (zoomInButton) zoomInButton.disabled = scale >= 3.99;
      if (zoomResetButton) zoomResetButton.textContent = scale <= 1.01 ? '1:1' : `${Math.round(scale * 100)}%`;
    };

    const resetZoom = (animate = true) => {
      scale = 1;
      translateX = 0;
      translateY = 0;
      renderZoom(animate);
    };

    const changeZoom = delta => {
      scale += delta;
      renderZoom(true);
    };

    const closeLightbox = () => {
      pointers.clear();
      stage?.classList.remove('is-dragging', 'is-pinching');
      setOpen(lightbox, false);
      window.setTimeout(() => {
        if (zoomImage && !lightbox.classList.contains('is-open')) zoomImage.removeAttribute('src');
      }, reducedMotion ? 0 : 300);
    };

    productZoomTriggers.forEach(trigger => trigger.addEventListener('click', () => {
      const sourceImage = $('img', trigger);
      const source = trigger.dataset.zoomSrc || sourceImage?.currentSrc || sourceImage?.src;
      if (!source || !zoomImage) return;
      zoomImage.alt = sourceImage?.alt || 'LEVORE PRIVÉ Produktbild';
      zoomImage.onload = () => resetZoom(false);
      resetZoom(false);
      zoomImage.src = source;
      setOpen(lightbox, true, trigger);
    }));

    $$('[data-product-zoom-close]', lightbox).forEach(button => button.addEventListener('click', closeLightbox));
    zoomInButton?.addEventListener('click', () => changeZoom(.5));
    zoomOutButton?.addEventListener('click', () => changeZoom(-.5));
    zoomResetButton?.addEventListener('click', () => resetZoom(true));

    stage?.addEventListener('dblclick', event => {
      event.preventDefault();
      if (scale > 1.01) resetZoom(true);
      else {
        scale = 2;
        renderZoom(true);
      }
    });

    stage?.addEventListener('wheel', event => {
      event.preventDefault();
      changeZoom(event.deltaY < 0 ? .3 : -.3);
    }, { passive: false });

    stage?.addEventListener('pointerdown', event => {
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      stage.setPointerCapture?.(event.pointerId);
      const values = Array.from(pointers.values());
      if (values.length === 1) {
        dragStartX = event.clientX;
        dragStartY = event.clientY;
        dragOriginX = translateX;
        dragOriginY = translateY;
        if (scale > 1.01) stage.classList.add('is-dragging');
      } else if (values.length === 2) {
        pinchStartDistance = pointerDistance(values);
        pinchStartScale = scale;
        stage.classList.remove('is-dragging');
        stage.classList.add('is-pinching');
      }
    });

    stage?.addEventListener('pointermove', event => {
      if (!pointers.has(event.pointerId)) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      const values = Array.from(pointers.values());
      if (values.length >= 2 && pinchStartDistance > 0) {
        scale = pinchStartScale * (pointerDistance(values.slice(0, 2)) / pinchStartDistance);
        renderZoom(false);
      } else if (values.length === 1 && scale > 1.01) {
        translateX = dragOriginX + (event.clientX - dragStartX);
        translateY = dragOriginY + (event.clientY - dragStartY);
        renderZoom(false);
      }
    });

    const releasePointer = event => {
      pointers.delete(event.pointerId);
      try { stage?.releasePointerCapture?.(event.pointerId); } catch (_) { /* Pointer capture may already be released. */ }
      const remaining = Array.from(pointers.values());
      if (remaining.length === 1) {
        dragStartX = remaining[0].x;
        dragStartY = remaining[0].y;
        dragOriginX = translateX;
        dragOriginY = translateY;
        stage?.classList.remove('is-pinching');
        if (scale > 1.01) stage?.classList.add('is-dragging');
      } else if (!remaining.length) {
        stage?.classList.remove('is-dragging', 'is-pinching');
        renderZoom(true);
      }
    };
    stage?.addEventListener('pointerup', releasePointer);
    stage?.addEventListener('pointercancel', releasePointer);

    window.addEventListener('resize', () => {
      if (lightbox.classList.contains('is-open')) renderZoom(false);
    });

    document.addEventListener('keydown', event => {
      if (!lightbox.classList.contains('is-open')) return;
      if (event.key === 'Escape') closeLightbox();
      if (event.key === '+' || event.key === '=') changeZoom(.5);
      if (event.key === '-') changeZoom(-.5);
      if (event.key === '0') resetZoom(true);
    });
  }

  if (body.classList.contains('has-page-transition') && !reducedMotion) {
    document.addEventListener('click', event => {
      const link = event.target.closest('a[href]');
      if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (link.target === '_blank' || link.hasAttribute('download')) return;
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return;
      if (url.protocol === 'mailto:' || url.protocol === 'tel:') return;
      event.preventDefault();
      body.classList.add('is-leaving');
      window.setTimeout(() => { window.location.href = url.href; }, 430);
    });
    window.addEventListener('pageshow', () => body.classList.remove('is-leaving'));
  }


  /* Subtle luxury parallax — desktop only, disabled for reduced motion. */
  if (!reducedMotion && window.matchMedia('(min-width: 900px)').matches) {
    const parallaxItems = $$('[data-luxury-parallax]');
    let parallaxTicking = false;
    const updateLuxuryParallax = () => {
      const viewport = window.innerHeight || 1;
      parallaxItems.forEach(item => {
        const rect = item.getBoundingClientRect();
        const progress = (rect.top + rect.height / 2 - viewport / 2) / viewport;
        const strength = Number(item.dataset.luxuryParallax || 0.04);
        item.style.setProperty('--lp-parallax-y', `${(-progress * strength * 240).toFixed(2)}px`);
      });
      parallaxTicking = false;
    };
    window.addEventListener('scroll', () => {
      if (parallaxTicking) return;
      parallaxTicking = true;
      requestAnimationFrame(updateLuxuryParallax);
    }, { passive: true });
    updateLuxuryParallax();
  }

})();
